import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { translations, clubsData, brandsData, playersData } from '@/translations';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  PhoneCall, 
  MessageSquare, 
  Mail, 
  CheckCircle, 
  Target, 
  Users, 
  TrendingUp,
  Award,
  Globe,
  Shield,
  Linkedin,
  Instagram,
  Twitter,
  Send,
  Languages
} from 'lucide-react';

const Home = () => {
  const { language, toggleLanguage } = useLanguage();
  const t = translations[language];
  
  const [formData, setFormData] = useState({
    name: '',
    contact: '',
    reason: '',
    message: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const { leadsAPI } = await import('@/services/api');
      await leadsAPI.createLead({
        name: formData.name,
        contact: formData.contact,
        lead_type: formData.reason,
        message: formData.message
      });
      
      toast.success("Solicitud enviada", {
        description: "Nos pondremos en contacto contigo pronto.",
      });
      setFormData({ name: '', contact: '', reason: '', message: '' });
    } catch (error) {
      toast.error("Error al enviar solicitud", {
        description: "Por favor intenta nuevamente.",
      });
    }
  };

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="home-container">
      {/* Navigation Header */}
      <header className="fixed-header">
        <nav className="nav-container">
          <a href="/" className="nav-logo-link">
            <img src="/ikons-logo.png" alt="Ikons Agency" className="nav-logo-image" />
          </a>
          <div className="nav-links">
            <a href="#servicios" className="nav-link">{t.nav.services}</a>
            <a href="#especializacion" className="nav-link">{t.nav.specialization}</a>
            <a href="#founder" className="nav-link">{t.nav.founder}</a>
            <a href="#faq" className="nav-link">{t.nav.faq}</a>
            <a href="#contact" className="nav-link-cta">{t.nav.contact}</a>
            <button 
              onClick={toggleLanguage} 
              className="lang-toggle"
              aria-label="Toggle language"
              data-testid="language-toggle"
            >
              <Languages className="lang-icon" />
              <span>{language === 'es' ? 'EN' : 'ES'}</span>
            </button>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-badge">{language === 'es' ? 'REPRESENTACIÓN DE ÉLITE' : 'ELITE REPRESENTATION'}</div>
          <h1 className="hero-title">{t.hero.title}</h1>
          <h2 className="hero-subtitle">{t.hero.subtitle}</h2>
          <div className="hero-taglines">
            {t.hero.tagline.map((line, idx) => (
              <p key={idx} className="hero-tagline">{line}</p>
            ))}
          </div>
          <div className="hero-ctas">
            <Button onClick={scrollToContact} className="btn-primary-hero">
              <PhoneCall className="btn-icon" />
              {t.hero.cta.primary}
            </Button>
            <Button 
              variant="outline" 
              className="btn-secondary-hero"
              onClick={() => window.open('https://wa.me/34665497561', '_blank')}
            >
              <MessageSquare className="btn-icon" />
              {t.hero.cta.secondary}
            </Button>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="vision-section">
        <div className="container-narrow">
          <h2 className="section-title-large">{t.vision.headline}</h2>
          <h3 className="section-subtitle-large">{t.vision.subheadline}</h3>
          <div className="vision-description">
            {t.vision.description.map((line, idx) => (
              <p key={idx} className="vision-line">{line}</p>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="about-section" id="sobre-nosotros">
        <div className="container">
          <h2 className="section-title">{t.about.title}</h2>
          <p className="section-intro">{t.about.description}</p>
          <div className="about-grid">
            {t.about.features.map((feature, idx) => (
              <div key={idx} className="about-card">
                <CheckCircle className="about-icon" />
                <p className="about-feature">{feature}</p>
              </div>
            ))}
          </div>
          <p className="section-tagline">{t.about.tagline}</p>
        </div>
      </section>

      {/* Services Section */}
      <section className="services-section" id="servicios">
        <div className="container">
          <h2 className="section-title">{t.services.title}</h2>
          <div className="services-grid">
            {t.services.list.map((service) => (
              <Card key={service.id} className={service.special ? "service-card-special" : "service-card"}>
                <CardContent className="service-content">
                  <h3 className="service-title">{service.title}</h3>
                  <p className="service-description">{service.description}</p>
                  <ul className="service-features">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="service-feature-item">
                        <CheckCircle className="feature-check" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="service-tagline">{service.tagline}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Specialization Section */}
      <section className="specialization-section" id="especializacion">
        <div className="container-narrow">
          <h2 className="section-title">{t.specialization.title}</h2>
          <div className="specialization-grid">
            {t.specialization.areas.map((area, idx) => (
              <div key={idx} className="specialization-card">
                <div className="specialization-number">0{idx + 1}</div>
                <p className="specialization-text">{area}</p>
              </div>
            ))}
          </div>
          <div className="section-tagline-group">
            {t.specialization.tagline.map((line, idx) => (
              <p key={idx} className="section-tagline">{line}</p>
            ))}
          </div>
          <div className="scouting-cta">
            <p className="scouting-cta-text">{t.scoutingCta.text}</p>
            <Link to="/scouting">
              <Button className="btn-scouting" data-testid="scouting-link">
                <Send className="btn-icon" />
                {t.scoutingCta.button}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Methodology Section */}
      <section className="methodology-section">
        <div className="container-narrow">
          <h2 className="section-title">{t.methodology.title}</h2>
          <div className="methodology-steps">
            {t.methodology.steps.map((step, idx) => (
              <div key={idx} className="methodology-step">
                <div className="step-number">{idx + 1}</div>
                <div className="step-content">
                  <p className="step-text">{step}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="section-tagline-group">
            {t.methodology.tagline.map((line, idx) => (
              <p key={idx} className="section-tagline">{line}</p>
            ))}
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="founder-section" id="founder">
        <div className="container-narrow">
          <h3 className="founder-label">{t.founder.title}</h3>
          <h2 className="founder-name">{t.founder.name}</h2>
          <p className="founder-subtitle">{t.founder.subtitle}</p>
          <p className="founder-description">{t.founder.description}</p>
          {t.founder.extendedDescription && (
            <div className="founder-extended">
              {t.founder.extendedDescription.map((line, idx) => (
                <p key={idx} className="founder-extended-text">{line}</p>
              ))}
            </div>
          )}
          <div className="founder-principles">
            <p className="founder-principles-title">{language === 'es' ? 'Su enfoque se basa en:' : 'His approach is based on:'}</p>
            <ul className="founder-principles-list">
              {t.founder.principles.map((principle, idx) => (
                <li key={idx} className="founder-principle">
                  <Shield className="principle-icon" />
                  {principle}
                </li>
              ))}
            </ul>
          </div>
          <div className="section-tagline-group">
            {t.founder.tagline.map((line, idx) => (
              <p key={idx} className="section-tagline founder-tagline">{line}</p>
            ))}
          </div>
        </div>
      </section>

      {/* Players Showcase Section */}
      <section className="players-showcase-section" data-testid="players-showcase">
        <div className="container">
          <div className="players-gallery">
            {playersData.map((player, idx) => (
              <div key={idx} className="player-showcase-card" data-testid={`player-card-${idx}`}>
                <div className="player-image-wrapper">
                  <img 
                    src={player.src} 
                    alt={player.alt[language]} 
                    className="player-showcase-image"
                  />
                  <div className="player-image-overlay"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Exclusive Section */}
      <section className="exclusive-section">
        <div className="container-narrow">
          <h2 className="section-title exclusive-title">{t.exclusive.title}</h2>
          <p className="exclusive-intro">{t.exclusive.description}</p>
          <p className="exclusive-statement">{t.exclusive.statement}</p>
          <div className="exclusive-criteria">
            <p className="criteria-label">{language === 'es' ? 'Evaluamos:' : 'We evaluate:'}</p>
            <div className="criteria-grid">
              {t.exclusive.criteria.map((criterion, idx) => (
                <div key={idx} className="criterion-item">
                  <TrendingUp className="criterion-icon" />
                  <span>{criterion}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="exclusive-philosophy">
            {t.exclusive.philosophy.map((line, idx) => (
              <p key={idx} className="philosophy-line">{line}</p>
            ))}
          </div>
        </div>
      </section>

      {/* Clubs Section */}
      <section className="clubs-section" data-testid="clubs-section">
        <div className="container">
          <h2 className="section-title">{t.clubs.title}</h2>
          <p className="section-intro">{t.clubs.subtitle}</p>
          <div className="clubs-grid">
            {clubsData.map((club, idx) => (
              <div key={idx} className="club-card" data-testid={`club-card-${idx}`}>
                <div className="club-logo-container">
                  <img 
                    src={club.logo} 
                    alt={`${club.name} logo`} 
                    className="club-logo"
                  />
                </div>
                <div className="club-info">
                  <div className="club-name">{club.name}</div>
                  <div className="club-country">{club.country[language]}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brands Section */}
      <section className="brands-section" data-testid="brands-section">
        <div className="container">
          <h2 className="section-title">{t.brands.title}</h2>
          <p className="section-intro">{t.brands.subtitle}</p>
          <div className="brands-grid">
            {brandsData.map((brand, idx) => (
              <div key={idx} className="brand-card" data-testid={`brand-card-${idx}`}>
                <div className="brand-logo-container">
                  <img 
                    src={brand.logo} 
                    alt={`${brand.name} logo`} 
                    className="brand-logo"
                  />
                </div>
                <div className="brand-name">{brand.name}</div>
                <div className="brand-category">{brand.category[language]}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section" id="faq">
        <div className="container-narrow">
          <h2 className="section-title">{t.faq.title}</h2>
          <Accordion type="single" collapsible className="faq-accordion">
            {t.faq.questions.map((faq, idx) => (
              <AccordionItem key={idx} value={`item-${idx}`} className="faq-item">
                <AccordionTrigger className="faq-question">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="faq-answer">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Contact Section */}
      <section className="contact-section" id="contact">
        <div className="container-narrow">
          <h2 className="section-title">{t.contact.title}</h2>
          <p className="contact-intro">
            {t.contact.subtitle}
          </p>
          <form onSubmit={handleSubmit} className="contact-form">
            <div className="form-group">
              <Label htmlFor="name">{t.contact.form.name}</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder={language === 'es' ? 'Tu nombre' : 'Your name'}
                required
              />
            </div>
            <div className="form-group">
              <Label htmlFor="contact">{t.contact.form.contact}</Label>
              <Input
                id="contact"
                name="contact"
                value={formData.contact}
                onChange={handleInputChange}
                placeholder={language === 'es' ? 'email@ejemplo.com o +34 XXX XXX XXX' : 'email@example.com or +34 XXX XXX XXX'}
                required
              />
            </div>
            <div className="form-group">
              <Label htmlFor="reason">{t.contact.form.interest}</Label>
              <Select name="reason" onValueChange={(value) => setFormData({...formData, reason: value})}>
                <SelectTrigger>
                  <SelectValue placeholder={language === 'es' ? 'Selecciona una opción' : 'Select an option'} />
                </SelectTrigger>
                <SelectContent>
                  {t.contact.form.interestOptions.map((option, idx) => (
                    <SelectItem key={idx} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="form-group">
              <Label htmlFor="message">{t.contact.form.message}</Label>
              <Textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                placeholder={t.contact.form.messagePlaceholder}
                rows={5}
                required
              />
            </div>
            <div className="form-actions">
              <Button type="submit" className="btn-submit">
                {t.contact.form.submit}
              </Button>
              <div className="direct-contact">
                <Button 
                  type="button" 
                  variant="outline" 
                  className="btn-whatsapp"
                  onClick={() => window.open('https://wa.me/34665497561', '_blank')}
                >
                  <MessageSquare className="btn-icon" />
                  WhatsApp
                </Button>
                <Button type="button" variant="outline" className="btn-email">
                  <Mail className="btn-icon" />
                  Email
                </Button>
              </div>
            </div>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-main">
            <div className="footer-brand">
              <img src="/ikons-logo.png" alt="Ikons Agency" className="footer-logo-image" />
              <div className="footer-taglines">
                {t.footer.tagline.map((line, idx) => (
                  <p key={idx} className="footer-tagline">{line}</p>
                ))}
              </div>
            </div>
            <div className="footer-social">
              <h4 className="footer-title">{language === 'es' ? 'Síguenos' : 'Follow Us'}</h4>
              <div className="social-links">
                <a href="#" className="social-link">
                  <Instagram className="social-icon" />
                  <span>@ikonsagency</span>
                </a>
                <a href="#" className="social-link">
                  <Linkedin className="social-icon" />
                  <span>Ikons Agency</span>
                </a>
                <a href="#" className="social-link">
                  <Twitter className="social-icon" />
                  <span>@ikonsagency</span>
                </a>
              </div>
            </div>
            <div className="footer-contact-info">
              <h4 className="footer-title">{language === 'es' ? 'Contacto' : 'Contact'}</h4>
              <p className="footer-contact-item">
                <Mail className="contact-icon" />
                contacto@ikonsagency.com
              </p>
              <p className="footer-contact-item">
                <MessageSquare className="contact-icon" />
                +34 665 497 561
              </p>
            </div>
          </div>
          <div className="footer-legal">
            <div className="legal-links">
              {t.footer.legal.map((link, idx) => (
                <a key={idx} href={link.url} className="legal-link">{link.label}</a>
              ))}
            </div>
            <p className="footer-copyright">
              © 2025 Ikons Agency. {language === 'es' ? 'Todos los derechos reservados.' : 'All rights reserved.'}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
